import { api } from './api/client';
import { deviceId } from './db';

/**
 * Turning notifications on, from the phone (API §9, ARCHITECTURE §12).
 *
 * The iOS constraints shape all of this and are not negotiable:
 *
 *   - Web Push works **only** once the app is on the home screen. In Safari's
 *     browser tab `PushManager` is not there at all, so the honest answer is
 *     "add it to your home screen first", not a button that fails.
 *   - `Notification.requestPermission()` must be called from a user gesture.
 *     Asking on load is both rejected by the browser and the thing that makes
 *     people say no forever.
 *
 * So this module is only ever called from a tap, and it reports which of those
 * situations it is in rather than returning a bare boolean.
 */

export type PushState =
  /** No `PushManager` — a browser tab on iOS, or an old browser. */
  | 'unavailable'
  /** The deployment has no VAPID keys; notifications are off for everyone. */
  | 'not_configured'
  | 'denied'
  | 'off'
  | 'on';

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * The public key, base64url as the server stores it, converted to the byte
 * array `subscribe()` wants. `atob` handles standard base64 only, hence the
 * two character swaps and the padding.
 */
function applicationServerKey(base64url: string): ArrayBuffer {
  const padded = base64url.padEnd(base64url.length + ((4 - (base64url.length % 4)) % 4), '=');
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  // The buffer, not the view: `Uint8Array<ArrayBufferLike>` does not satisfy
  // `BufferSource` under the current lib.dom, and this is the same bytes.
  return bytes.buffer;
}

async function serverKey(): Promise<string | null> {
  const { key } = await api<{ key: string | null }>('/api/v1/push/key');
  return key;
}

/** What the settings row should say right now, without asking for anything. */
export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return 'unavailable';
  if (Notification.permission === 'denied') return 'denied';
  if ((await serverKey()) === null) return 'not_configured';

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  return existing ? 'on' : 'off';
}

/**
 * Subscribes this phone. Must be called from a click handler.
 *
 * The subscription goes to the server keyed by device id, which the sync layer
 * already generates and keeps in IndexedDB — the same identity that stamps
 * `updated_by_device`, so a notification is suppressed on the phone that made
 * the change without needing a second notion of "this device".
 *
 * `requestPermission()` is the **first** thing this does, before any awaited
 * network call, and the order is load-bearing on iOS. Safari/WebKit grants the
 * permission prompt only while the tap's *transient user activation* is still
 * live, and an awaited `fetch` (for the VAPID key) spends it — so asking the
 * server for the key first meant iOS silently never showed the prompt and the
 * button looked dead. Chrome keeps the activation across the fetch, which is why
 * this only ever failed on iPhones. The key is fetched *after* the grant, where
 * no activation is needed. See API §9 and the ordering regression in
 * `web/test/push.test.ts`.
 */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unavailable';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  const key = await serverKey();
  if (key === null) return 'not_configured';

  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      // Required by every browser, and the only value it can take: a
      // subscription that could deliver a silent push would be revoked.
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey(key),
    }));

  await api('/api/v1/devices', {
    method: 'POST',
    body: {
      device_id: await deviceId(),
      label: navigator.userAgent.slice(0, 200),
      push: subscription.toJSON(),
    },
  });

  return 'on';
}

/**
 * Unsubscribes, on the phone and on the server.
 *
 * Both halves matter. Dropping only the browser subscription leaves the
 * endpoint in `devices`, where the sweep keeps pushing to it — nothing appears
 * on the phone, so it looks off, but every notification is still being sent
 * and the toggle is a lie.
 */
export async function disablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unavailable';

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  await subscription?.unsubscribe();

  await api('/api/v1/push/subscription', {
    method: 'DELETE',
    body: { device_id: await deviceId() },
  });

  return 'off';
}
